import { useEffect, useState } from 'react';
import { globalSettingService, SupportedLanguage } from '../api/globalSettings';

// One fetch of the supported-languages list per page load, shared by every caller.
let languagesPromise: Promise<SupportedLanguage[]> | null = null;
export function loadLanguages(): Promise<SupportedLanguage[]> {
    if (!languagesPromise) {
        languagesPromise = globalSettingService.getSupportedLanguages().catch((err) => {
            languagesPromise = null; // allow a retry on the next mount
            throw err;
        });
    }
    return languagesPromise;
}

export function resolveLanguageCode(value: string, languages: SupportedLanguage[]): string | undefined {
    const v = value.trim().toLowerCase();
    const match = languages.find(
        (l) => l.code.toLowerCase() === v || l.name.toLowerCase() === v || (l.native_name ?? '').toLowerCase() === v,
    );
    return match?.code.toLowerCase();
}

/**
 * Maps a stored language value to its ISO code. Lessons store the display NAME ("French" — what
 * CreateLesson's <select> submits and the generation prompt wants), but translation, word lookup
 * and vocabulary all use codes ("fr"). A value that already is a code passes through. Returns
 * undefined while the list loads; `fallback` once it has loaded (or failed to) without a match,
 * or when there's no value at all.
 */
export function useLanguageCode(value: string | undefined, fallback?: string): string | undefined {
    // resolved: undefined = still loading; null = settled with no match.
    const [resolved, setResolved] = useState<string | null | undefined>(undefined);
    useEffect(() => {
        setResolved(undefined);
        if (!value) return;
        let cancelled = false;
        loadLanguages()
            .then((langs) => { if (!cancelled) setResolved(resolveLanguageCode(value, langs) ?? null); })
            .catch(() => { if (!cancelled) setResolved(null); });
        return () => { cancelled = true; };
    }, [value]);
    if (!value) return fallback;
    if (resolved === null) return fallback;
    return resolved;
}
